/**
 * Tonstufen und Farbpaletten der Animationen.
 *
 * Gemäß der Stilentscheidung „einfarbige Piktogramme“ gibt es nur Stufen eines
 * warmen Neutraltons und einen einzigen Akzent, der Hitze und Flammen
 * vorbehalten ist. Alle Flächen sind deckend: Tiefe entsteht über die Stufen,
 * nie über Transparenz. Dadurch scheint nichts durch.
 */

/** Alle Tonstufen mit Farbe, von hell nach dunkel, dann der Akzent. */
export const TONES = [
  'backdrop',
  'paper',
  'tint',
  'shade',
  'deep',
  'ink',
  'shadow',
  'cavity',
  'snow',
  'accent',
  'accentTint',
] as const;

/** Eine Tonstufe. "none" heißt: keine Füllung bzw. keine Kontur. */
export type Tone = (typeof TONES)[number] | 'none';

/** Konkrete Farben je Tonstufe. */
export type Palette = Readonly<Record<(typeof TONES)[number], string>>;

/** Helle Darstellung, abgestimmt auf die Farben der App. */
export const LIGHT_PALETTE: Palette = {
  backdrop: '#f6f1ea',
  paper: '#ffffff',
  tint: '#eee6dc',
  shade: '#d8c9b9',
  deep: '#9b8877',
  ink: '#2b2522',
  shadow: '#e4d9cd',
  /** Dunkler Innenraum (Ofen, Topf) – in beiden Modi dunkel. */
  cavity: '#3d3430',
  /** Heller Stoff (Eischnee, Sahne, Mehl, Anzeigeschrift) – in beiden Modi hell. */
  snow: '#fffdf9',
  accent: '#e8590c',
  accentTint: '#ffc69a',
};

/**
 * Dunkle Darstellung. Gegenstände sind dunkle Flächen mit heller Kontur; die
 * Abstände der Stufen bleiben gleich, damit die Bilder gleich gelesen werden.
 */
export const DARK_PALETTE: Palette = {
  backdrop: '#1f1b19',
  paper: '#3b3430',
  tint: '#4a413b',
  shade: '#5f544c',
  deep: '#938377',
  ink: '#f1ebe4',
  shadow: '#151210',
  cavity: '#171311',
  snow: '#ece5dc',
  accent: '#ff8a3d',
  accentTint: '#f2ab78',
};

/** Schrift für Anzeigen in den Bildern: serifenlos und kräftig. */
export const FONT_FAMILY = "system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif";

/** Strichstärken. Mehr gibt es nicht, damit alle Bilder gleich wirken. */
export const STROKE = {
  /** Umrisse aller Gegenstände. */
  outline: 3,
  /** Innere Linien, Kanten, Schnitte. */
  detail: 2,
  /** Feine Strukturen wie Maserung oder Schale. */
  fine: 1.25,
} as const;

/** Erlaubte Strichstärken als Liste, für Prüfungen. */
export const STROKE_WIDTHS: readonly number[] = [STROKE.outline, STROKE.detail, STROKE.fine];

/** Feste Zeichenfläche aller Animationen. */
export const CANVAS = { width: 240, height: 160 } as const;

/** Mindestgröße für Text in den Bildern, damit er auf dem Telefon lesbar bleibt. */
export const MIN_TEXT_SIZE = 13;
